import moment from 'moment';
import { getCurrencySymbol } from 'src/lib/utils';
import { formatPricingAmount } from 'src/pages/subscription-new/utils/plans.util';
import { EffectivePlanObjType } from 'src/pages/payment-new/types';
import { Plan } from 'src/pages/deprecated/payment/types';
import { ReactivationSubscriptionDetails } from 'src/services/reactivation.service';
import { ReactivationCampaignType } from 'src/services/reactivation.types';
import { getTargetPlanId } from './reactivationUtils';
import { DEFAULT_DISCOUNT_ROLLOVER_PERCENT } from '../constants';

export const DEFAULT_RENEWAL_DESCRIPTION =
  'Your subscription will automatically renew until cancelled.';

export interface DiscountRolloverAmounts {
  originalBillingAmount?: string;
  billingAmountOverride?: string;
}

export const resolveOrderSummaryPlanId = (
  selectedPlanId: string | undefined,
  subscription:
    | {
        planId?: string;
        priceId?: string;
        planName?: string;
      }
    | null
    | undefined,
  allPlansSorted: Plan[]
): string => {
  if (
    selectedPlanId &&
    allPlansSorted.some((p: Plan) => p.id === selectedPlanId)
  ) {
    return selectedPlanId;
  }
  return (
    getTargetPlanId(subscription ?? null, allPlansSorted) ||
    selectedPlanId ||
    ''
  );
};

export const getDiscountRolloverAmounts = (
  isDiscountRollover: boolean,
  effectivePlanObj: EffectivePlanObjType | null | undefined,
  subscription: ReactivationSubscriptionDetails | null | undefined,
  resolvedPlanId: string
): DiscountRolloverAmounts => {
  if (!isDiscountRollover || !effectivePlanObj) {
    return {
      originalBillingAmount: undefined,
      billingAmountOverride: undefined,
    };
  }

  const percentOff =
    subscription?.invoiceDiscount?.percent_off ??
    DEFAULT_DISCOUNT_ROLLOVER_PERCENT;
  const fullUnitAmount = Number(effectivePlanObj.unitAmount);
  const apiOriginal = subscription?.originalTotalPrice;

  const isInitialPlan =
    resolvedPlanId === subscription?.planId ||
    resolvedPlanId === subscription?.priceId;

  if (isInitialPlan && (apiOriginal || subscription?.totalPrice != null)) {
    return {
      originalBillingAmount: Number(apiOriginal ?? fullUnitAmount).toFixed(2),
      billingAmountOverride: Number(
        subscription?.totalPrice ?? fullUnitAmount * (1 - percentOff / 100)
      ).toFixed(2),
    };
  }
  if (percentOff) {
    return {
      originalBillingAmount: fullUnitAmount.toFixed(2),
      billingAmountOverride: (fullUnitAmount * (1 - percentOff / 100)).toFixed(
        2
      ),
    };
  }
  return {
    originalBillingAmount: undefined,
    billingAmountOverride: undefined,
  };
};

export interface ReactivationOrderDescriptionParams {
  showFreeTrial?: boolean;
  campaignType?: ReactivationCampaignType | null;
  effectivePlanObj?: EffectivePlanObjType | null;
  baseDate?: moment.Moment;
  discountedAmount?: string | number | null;
}

export const formatBillingPeriod = (
  intervalCount: number = 1,
  interval: string = 'month'
): string => {
  if (intervalCount === 1) {
    return interval;
  }
  return `${intervalCount} ${interval}s`;
};

export const getReactivationOrderDescription = ({
  showFreeTrial = false,
  campaignType,
  effectivePlanObj,
  baseDate,
  discountedAmount,
}: ReactivationOrderDescriptionParams): string => {
  if (!effectivePlanObj) {
    return DEFAULT_RENEWAL_DESCRIPTION;
  }

  const isFreeMonth =
    showFreeTrial || campaignType === ReactivationCampaignType.FREE_MONTH;
  const isDiscountRollover =
    campaignType === ReactivationCampaignType.DISCOUNT_ROLLOVER;

  if (!isFreeMonth && !isDiscountRollover) {
    return DEFAULT_RENEWAL_DESCRIPTION;
  }

  const now = baseDate ? baseDate.clone() : moment();
  let nextBillingDate: string;

  if (isFreeMonth) {
    // FREE_MONTH: charged 0 for now. Next billing is 1 month from now.
    nextBillingDate = now.add(1, 'month').format('MMMM D, YYYY');
  } else {
    // DISCOUNT_ROLLOVER: charged discounted price for the plan interval (1, 3, or 6 months).
    // Next billing is after that interval, at the discounted price.
    const intervalCount =
      effectivePlanObj.intervalCount && effectivePlanObj.intervalCount > 0
        ? effectivePlanObj.intervalCount
        : 1;
    const intervalUnit = (effectivePlanObj.interval ||
      'month') as moment.DurationInputArg2;
    nextBillingDate = now
      .add(intervalCount, intervalUnit)
      .format('MMMM D, YYYY');
  }

  const sym = getCurrencySymbol();
  const rawAmount =
    isDiscountRollover && discountedAmount != null
      ? discountedAmount
      : effectivePlanObj.unitAmount;
  const amount = rawAmount != null ? formatPricingAmount(rawAmount) : '0';
  const interval = formatBillingPeriod(
    effectivePlanObj.intervalCount ?? 1,
    effectivePlanObj.interval ?? 'month'
  );

  return `Then billed at ${sym}${amount}/${interval} from ${nextBillingDate}. Your subscription will automatically renew until cancelled.`;
};
